import { createClientConfig, createStaticConfigProvider, libConfig } from '@sstdev/lib_config';
import getBrand from './getBrand';
import getEnvironment from './getEnvironment';
import getHostName from './getHostname';
import getPort from './getPort';
import getProtocol from './getProtocol';
import isDeployed from './isDeployed';

const _p = {
    getBrand,
    getEnvironment,
    getHostName,
    getPort,
    getProtocol,
    isDeployed
};
export const _private = _p;

/**
 * Resolves location-derived UI identity through the same config facade used for
 * env-backed runtime values.
 *
 * In this file, "identity" means the identity of the running silo_ui client:
 * which brand it is serving, which environment it is pointed at, and which
 * protocol/host values should be used when building URLs. It is not user
 * identity, Auth0 identity, tenant identity, or security/session state.
 *
 * The underlying helpers still own browser/native details such as hostname
 * overrides and `.native.js` behavior. This adapter only gives consumers one
 * read-only config path so new code does not need to depend on `globalConfig()`
 * for stable runtime identity values.
 *
 * @param {object} [options]
 * @param {boolean} [options.productionBuild]
 * @returns {UiRuntimeIdentityConfig}
 */
export function getUiRuntimeIdentityConfig({ productionBuild } = {}) {
    const derivedIdentityValues = getDerivedIdentityValues(productionBuild);
    const { getConfig } = createClientConfig({
        provider: createStaticConfigProvider({
            name: 'ui-runtime-identity',
            values: derivedIdentityValues
        })
    });

    return {
        brand: getConfig(libConfig.ui.identity.brand),
        environment: getConfig(libConfig.ui.identity.environment),
        host: getConfig(libConfig.ui.identity.host),
        hostname: getConfig(libConfig.ui.identity.hostname),
        isDeployed: getConfig(libConfig.ui.identity.isDeployed),
        port: getConfig(libConfig.ui.identity.port),
        protocol: getConfig(libConfig.ui.identity.protocol),
        protocolSocket: getConfig(libConfig.ui.identity.protocolSocket)
    };
}

/**
 * @param {boolean | undefined} productionBuild
 * @returns {Record<string, string | number | boolean>}
 */
function getDerivedIdentityValues(productionBuild) {
    // Keep this derivation in the existing helpers so browser and React Native
    // continue to agree on hostname overrides, local/deployed detection, and
    // protocol behavior while callers migrate away from `globalConfig()`.
    const hostname = _p.getHostName(productionBuild);
    const deployed = _p.isDeployed(hostname);
    const port = _p.getPort(productionBuild);

    return {
        [libConfig.ui.identity.brand.key]: _p.getBrand(hostname),
        [libConfig.ui.identity.environment.key]: _p.getEnvironment(productionBuild),
        [libConfig.ui.identity.host.key]: `${hostname}${deployed ? '' : ':'}${port}`,
        [libConfig.ui.identity.hostname.key]: hostname,
        [libConfig.ui.identity.isDeployed.key]: deployed,
        [libConfig.ui.identity.port.key]: port,
        [libConfig.ui.identity.protocol.key]: _p.getProtocol(productionBuild),
        [libConfig.ui.identity.protocolSocket.key]: _p.getProtocol(productionBuild, 'ws')
    };
}

/**
 * @typedef {object} UiRuntimeIdentityConfig
 * Runtime/deployment identity for the current silo_ui client.
 *
 * These values answer "where is this UI running and what backend origin should
 * it target?" They intentionally do not describe the logged-in user, selected
 * tenant, selected use case, group number, or mutable session-derived state.
 *
 * @property {string} brand
 * @property {string} environment
 * @property {string} host
 * @property {string} hostname
 * @property {boolean} isDeployed
 * @property {string | number} port
 * @property {string} protocol
 * @property {string} protocolSocket
 */
